import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const ACTIVITIES = ["course", "preschool_prep", "kindergarten"];
const PAYMENT_MODELS = ["monthly", "per_course", "per_lesson_package"] as const;
const INVITE_ROLES = ["branch_manager", "administrator", "accountant", "academic_lead", "teacher"];

export default function Onboarding() {
    const { t } = useTranslation();
    const { activeMembership, refresh } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState<number>(1);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // form state
    const [instName, setInstName] = useState("");
    const [activities, setActivities] = useState<string[]>([]);
    const [country, setCountry] = useState("AZ");
    const [language, setLanguage] = useState("az");
    const [currency, setCurrency] = useState("AZN");
    const [timezone, setTimezone] = useState("Asia/Baku");
    const [branchName, setBranchName] = useState("");
    const [branchAddress, setBranchAddress] = useState("");
    const [branchPhone, setBranchPhone] = useState("");
    const [teaching, setTeaching] = useState("monthly");
    // Phase PM-1: onboarding now selects one *or more* payment models.
    const [paymentModels, setPaymentModels] = useState<string[]>([]);
    const [invites, setInvites] = useState<{ email: string; role_code: string }[]>([]);
    const [inviteResults, setInviteResults] = useState<any[]>([]);
    const [addDemoData, setAddDemoData] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/onboarding/state");
                setStep(data.onboarding_step || 1);
                if (data.data.institution_name) setInstName(data.data.institution_name);
                if (data.data.activity_types?.length) setActivities(data.data.activity_types);
                if (data.data.country) setCountry(data.data.country);
                if (data.data.language) setLanguage(data.data.language);
                if (data.data.currency) setCurrency(data.data.currency);
                if (data.data.timezone) setTimezone(data.data.timezone);
                if (data.data.teaching_model) setTeaching(data.data.teaching_model);
                // Phase PM-1: prefer the canonical array; fall back to the
                // legacy singular so previously-onboarded tenants see their
                // selection reflected as a single-entry list.
                if (Array.isArray(data.data.payment_models) && data.data.payment_models.length) {
                    setPaymentModels(data.data.payment_models);
                } else if (data.data.teaching_model) {
                    setPaymentModels([data.data.teaching_model]);
                }
            } catch (e: any) {
                setErr(formatApiError(e));
            }
        })();
    }, []);

    const done = activeMembership?.tenant.onboarding_completed;
    useEffect(() => {
        if (done) navigate("/", { replace: true });
    }, [done, navigate]);

    const call = async (path: string, body: any, next?: number) => {
        setLoading(true);
        setErr(null);
        try {
            const r = await api.post(path, body);
            if (path === "/onboarding/step5") setInviteResults(r.data.invites || []);
            if (next != null) setStep(next);
            if (path === "/onboarding/step6") await refresh();
            return r.data;
        } catch (e: any) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const stepLabels = [1, 2, 3, 4, 5, 6];

    return (
        <div className="min-h-screen p-6 md:p-12" style={{ background: "var(--page)" }} data-testid="onboarding-page">
            <div className="max-w-3xl mx-auto">
                <h1 className="text-3xl font-semibold mb-1" style={{ color: "var(--text)" }} data-testid="onboarding-title">{t("onboarding.title")}</h1>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>{t("onboarding.subtitle")}</p>

                <div className="flex items-center gap-2 mt-6 mb-8" data-testid="onboarding-stepper">
                    {stepLabels.map((n) => (
                        <div key={n} className="flex-1 flex items-center gap-2">
                            <div
                                className="rounded-full flex items-center justify-center text-xs font-semibold shrink-0"
                                style={{
                                    width: 28, height: 28,
                                    background: n <= step ? "var(--primary)" : "var(--input)",
                                    color: n <= step ? "var(--primary-fg)" : "var(--text-muted)",
                                    border: `1px solid ${n <= step ? "var(--primary)" : "var(--border)"}`,
                                }}
                                data-testid={`step-indicator-${n}`}
                            >
                                {n < step ? <CheckCircle2 size={14} /> : n}
                            </div>
                            {n < 6 && <div className="flex-1 h-px" style={{ background: n < step ? "var(--primary)" : "var(--border)" }} />}
                        </div>
                    ))}
                </div>

                <div className="surface-card p-6">
                    {err && <div className="mb-4 rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="onboarding-error">{err}</div>}

                    {step === 1 && (
                        <div className="space-y-4" data-testid="step-1">
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("onboarding.step1.title")}</h2>
                            <label className="block">
                                <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("onboarding.institution_name")}</div>
                                <input data-testid="inst-name-input" required value={instName} onChange={(e) => setInstName(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                            </label>
                            <div>
                                <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("onboarding.activity_types")}</div>
                                <div className="flex flex-wrap gap-2">
                                    {ACTIVITIES.map((a) => {
                                        const on = activities.includes(a);
                                        return (
                                            <button key={a} type="button" data-testid={`activity-${a}`}
                                                onClick={() => setActivities((v) => (on ? v.filter((x) => x !== a) : [...v, a]))}
                                                className="rounded-full px-4 py-2 text-sm border transition-colors"
                                                style={{
                                                    background: on ? "var(--selected-bg)" : "var(--input)",
                                                    color: on ? "var(--text)" : "var(--text-muted)",
                                                    borderColor: on ? "var(--primary)" : "var(--border)",
                                                    minHeight: 44,
                                                }}>{t(`activity.${a}`)}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <NextBtn testId="step1-submit" loading={loading} disabled={!instName.trim() || activities.length === 0} onClick={() => call("/onboarding/step1", { institution_name: instName, activity_types: activities }, 2)} />
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4" data-testid="step-2">
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("onboarding.step2.title")}</h2>
                            <SelectField label={t("onboarding.country")} value={country} onChange={setCountry} testId="country-select" options={[{v:"AZ",l:"Azərbaycan"},{v:"TR",l:"Türkiye"},{v:"US",l:"United States"}]} />
                            <SelectField label={t("onboarding.language")} value={language} onChange={setLanguage} testId="language-select" options={[{v:"az",l:"Azərbaycanca"},{v:"en",l:"English"}]} />
                            <SelectField label={t("onboarding.currency")} value={currency} onChange={setCurrency} testId="currency-select" options={[{v:"AZN",l:"AZN"},{v:"USD",l:"USD"},{v:"EUR",l:"EUR"},{v:"TRY",l:"TRY"}]} />
                            <SelectField label={t("onboarding.timezone")} value={timezone} onChange={setTimezone} testId="timezone-select" options={[{v:"Asia/Baku",l:"Asia/Baku"},{v:"Europe/Istanbul",l:"Europe/Istanbul"},{v:"UTC",l:"UTC"}]} />
                            <div className="flex gap-2 pt-2">
                                <BackBtn onClick={() => setStep(1)} />
                                <NextBtn testId="step2-submit" loading={loading} onClick={() => call("/onboarding/step2", { country, language, currency, timezone, date_format: "dd.MM.yyyy" }, 3)} />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4" data-testid="step-3">
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("onboarding.step3.title")}</h2>
                            <TextField label={t("onboarding.branch_name")} value={branchName} onChange={setBranchName} testId="branch-name-input" required />
                            <TextField label={t("onboarding.branch_address")} value={branchAddress} onChange={setBranchAddress} testId="branch-address-input" />
                            <TextField label={t("onboarding.branch_phone")} value={branchPhone} onChange={setBranchPhone} testId="branch-phone-input" />
                            <div className="flex gap-2 pt-2">
                                <BackBtn onClick={() => setStep(2)} />
                                <NextBtn testId="step3-submit" loading={loading} disabled={!branchName.trim()} onClick={() => call("/onboarding/step3", { branch_name: branchName, address: branchAddress || null, phone: branchPhone || null }, 4)} />
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-4" data-testid="step-4">
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("onboarding.step4.title")}</h2>
                            <p className="text-sm" style={{ color: "var(--text-muted)" }}>{t("onboarding.step4.desc")}</p>
                            <div className="grid gap-3 md:grid-cols-3">
                                {PAYMENT_MODELS.map((m: string) => {
                                    const on = paymentModels.includes(m);
                                    return (
                                        <button key={m} type="button" data-testid={`payment-model-${m}`}
                                            onClick={() => setPaymentModels((v) => (on ? v.filter((x) => x !== m) : [...v, m]))}
                                            className="text-left rounded-md p-4 border transition-colors"
                                            style={{
                                                background: on ? "var(--selected-bg)" : "var(--input)",
                                                borderColor: on ? "var(--primary)" : "var(--border)",
                                                minHeight: 44,
                                            }}>
                                            <div className="flex items-start gap-2">
                                                <span className="rounded-md inline-flex items-center justify-center shrink-0" style={{ width: 20, height: 20, background: on ? "var(--primary)" : "var(--card)", color: on ? "var(--primary-fg)" : "var(--text-muted)", borderColor: on ? "var(--primary)" : "var(--border)", border: "1px solid" }}>
                                                    {on ? <CheckCircle2 size={14} /> : null}
                                                </span>
                                                <div>
                                                    <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{t(`teaching.${m}`)}</div>
                                                    <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{t(`teaching.desc.${m}`)}</div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {paymentModels.length === 0 && (
                                <p className="text-xs" data-testid="payment-model-required" style={{ color: "var(--warning-fg)" }}>{t("onboarding.step4.pick_at_least_one")}</p>
                            )}
                            <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("onboarding.step4.note")}</p>
                            <div className="flex gap-2 pt-2">
                                <BackBtn onClick={() => setStep(3)} />
                                <NextBtn testId="step4-submit" loading={loading} disabled={paymentModels.length === 0}
                                    onClick={() => call("/onboarding/step4", { payment_models: paymentModels }, 5)} />
                            </div>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="space-y-4" data-testid="step-5">
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("onboarding.step5.title")}</h2>
                            <p className="text-xs" style={{ color: "var(--text-muted)" }}>{t("onboarding.step5.desc")}</p>
                            {invites.map((inv, i) => (
                                <div key={i} className="flex gap-2 items-end" data-testid={`invite-row-${i}`}>
                                    <div className="flex-1">
                                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("auth.email")}</div>
                                        <input data-testid={`invite-email-${i}`} type="email" value={inv.email} onChange={(e) => setInvites((v) => v.map((x, k) => k === i ? { ...x, email: e.target.value } : x))} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                                    </div>
                                    <div>
                                        <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("staff.role")}</div>
                                        <select data-testid={`invite-role-${i}`} value={inv.role_code} onChange={(e) => setInvites((v) => v.map((x, k) => k === i ? { ...x, role_code: e.target.value } : x))} className="surface-input px-3 py-2 text-sm" style={{ minHeight: 44 }}>
                                            {INVITE_ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
                                        </select>
                                    </div>
                                    <button type="button" data-testid={`invite-remove-${i}`} onClick={() => setInvites((v) => v.filter((_, k) => k !== i))} className="rounded-md px-3 py-2 text-sm border" style={{ borderColor: "var(--border)", color: "var(--text-muted)", minHeight: 44 }}>{t("common.remove")}</button>
                                </div>
                            ))}
                            <button type="button" data-testid="add-invite-btn" onClick={() => setInvites((v) => [...v, { email: "", role_code: "teacher" }])} className="text-sm rounded-md px-3 py-2 border" style={{ borderColor: "var(--border)", color: "var(--text)", background: "var(--input)", minHeight: 44 }}>+ {t("onboarding.add_invite")}</button>

                            {inviteResults.length > 0 && (() => {
                                const sent = inviteResults.filter((r: any) => r.email_status === "sent").length;
                                const failed = inviteResults.length - sent;
                                const allSent = failed === 0;
                                const allFailed = sent === 0;
                                const bg = allSent ? "var(--success-bg)" : allFailed ? "var(--error-bg)" : "var(--warning-bg)";
                                const fg = allSent ? "var(--success-fg)" : allFailed ? "var(--error-fg)" : "var(--warning-fg)";
                                const title = allSent
                                    ? t("onboarding.step5.email_sent")
                                    : allFailed
                                        ? t("onboarding.step5.email_failed")
                                        : t("onboarding.step5.email_partial", { sent, total: inviteResults.length });
                                return (
                                    <div className="rounded-md p-3 text-sm" style={{ background: bg, color: fg }} data-testid="invite-results">
                                        <div className="font-medium mb-1">{title}</div>
                                        {!allSent && (
                                            <div className="text-xs mb-2 opacity-90">{t("onboarding.step5.fallback_link_note")}</div>
                                        )}
                                        <ul className="space-y-1">
                                            {inviteResults.map((r: any) => (
                                                <li key={r.id} className="font-mono text-xs break-all">
                                                    {r.email}
                                                    {r.email_status === "sent"
                                                        ? <> · <span data-testid={`invite-sent-${r.email}`}>{t("onboarding.step5.email_sent_row")}</span></>
                                                        : r.fallback_link
                                                            ? <> → {r.fallback_link}</>
                                                            : null}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })()}
                            <div className="flex gap-2 pt-2 flex-wrap">
                                <BackBtn onClick={() => setStep(4)} />
                                <button type="button" data-testid="skip-invites-btn" onClick={() => call("/onboarding/step5", { invites: [] }, 6)} className="rounded-md px-4 py-2 text-sm border" style={{ borderColor: "var(--border)", color: "var(--text)", minHeight: 44 }}>{t("onboarding.skip")}</button>
                                <NextBtn testId="step5-submit" loading={loading} onClick={() => call("/onboarding/step5", { invites }, invites.length ? 5 : 6)} label={invites.length ? t("onboarding.send_invites") : undefined} disabled={invites.some((i) => !i.email)} />
                            </div>
                        </div>
                    )}

                    {step === 6 && (
                        <div className="space-y-4" data-testid="step-6">
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t("onboarding.step6.title")}</h2>
                            <div className="flex flex-col gap-2">
                                <label className="flex items-start gap-3 rounded-md p-3 cursor-pointer border" style={{ background: !addDemoData ? "var(--selected-bg)" : "var(--input)", borderColor: !addDemoData ? "var(--primary)" : "var(--border)" }} data-testid="option-empty">
                                    <input type="radio" name="startmode" checked={!addDemoData} onChange={() => setAddDemoData(false)} />
                                    <div>
                                        <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("onboarding.start_empty")}</div>
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("onboarding.start_empty_desc")}</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-3 rounded-md p-3 cursor-pointer border" style={{ background: addDemoData ? "var(--selected-bg)" : "var(--input)", borderColor: addDemoData ? "var(--primary)" : "var(--border)" }} data-testid="option-demo">
                                    <input type="radio" name="startmode" checked={addDemoData} onChange={() => setAddDemoData(true)} />
                                    <div>
                                        <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("onboarding.add_demo_data")}</div>
                                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("onboarding.add_demo_data_desc")}</div>
                                    </div>
                                </label>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <BackBtn onClick={() => setStep(5)} />
                                <NextBtn testId="step6-submit" loading={loading} onClick={async () => { await call("/onboarding/step6", { add_demo_data: addDemoData }); navigate("/", { replace: true }); }} label={t("onboarding.finish")} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function TextField({ label, value, onChange, testId, required }: any) {
    return (
        <label className="block">
            <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
            <input data-testid={testId} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
        </label>
    );
}

function SelectField({ label, value, onChange, options, testId }: any) {
    return (
        <label className="block">
            <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
            <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }}>
                {options.map((o: any) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </label>
    );
}

function NextBtn({ onClick, loading, disabled, testId, label }: any) {
    const { t } = useTranslation();
    return (
        <button data-testid={testId} type="button" disabled={loading || disabled} onClick={onClick}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60 transition-colors ml-auto"
            style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {label || t("common.next")}
            <ChevronRight size={16} />
        </button>
    );
}

function BackBtn({ onClick }: any) {
    const { t } = useTranslation();
    return (
        <button data-testid="back-btn" type="button" onClick={onClick} className="rounded-md px-4 py-2 text-sm border" style={{ borderColor: "var(--border)", color: "var(--text)", background: "var(--input)", minHeight: 44 }}>
            {t("common.back")}
        </button>
    );
}
